import { api, WorkspacesAPITagType } from '@/api/api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import defaultStringifyRequest from '@/constants/default-stringify-request'
import { RootState } from '@/store/store'
import {
  fetchKolCollectionKolsResponseSchema,
  FetchKolCollectionSimpleKolsRequest,
  FetchKolCollectionSimpleKolsResponse,
  fetchKolCollectionSimpleKolsResponseSchema,
  type FetchKolCollectionKolsRequest,
  type FetchKolCollectionKolsResponse,
} from '@/types/api/collections'
import {
  createKolCollectionResponseSchema,
  fetchKolCollectionSettingResponseSchema,
  fetchKolCollectionsResponseSchema,
  kolCollectionSchema,
  type AddKolsToKolCollectionRequest,
  type CreateKolCollectionRequest,
  type CreateKolCollectionResponse,
  type DeleteKolCollectionRequest,
  type DeleteKolsFromKolCollectionRequest,
  type DuplicateKolCollectionRequest,
  type ExportKolCollectionRequest,
  type FetchKolCollectionRequest,
  type FetchKolCollectionResponse,
  type FetchKolCollectionSettingRequest,
  type FetchKolCollectionSettingResponse,
  type FetchKolCollectionsInfiniteRequest,
  type FetchKolCollectionsRequest,
  type FetchKolCollectionsResponse,
  type SetKolCollectionSettingRequest,
  type UpdateKolCollectionRequest,
} from '@/types/schema/api/kol-collections'
import { filterResourceType } from '@/types/schema/directory/kol-management'
import logger from '@/utils/logger'
import removeNil from '@/utils/remove-nil'
import updateKolCollectionKolQueryData from '@/utils/update-kol-collection-kol-query-data'

const kolCollectionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createKolCollection: builder.mutation<
      CreateKolCollectionResponse,
      CreateKolCollectionRequest
    >({
      query: (body) => {
        return {
          url: getWorkspaceApiUrl('/kol-collections', 'v3'),
          method: 'POST',
          body,
        }
      },
      invalidatesTags: (_result, _error, arg) => {
        const filterResourceTag = {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.collection,
        }

        return arg.uuid
          ? [
              { type: WorkspacesAPITagType.Collections, id: arg.uuid },
              WorkspacesAPITagType.Collections,
              filterResourceTag,
            ]
          : [WorkspacesAPITagType.Collections, filterResourceTag]
      },
      extraOptions: {
        dataSchema: createKolCollectionResponseSchema,
      },
    }),
    deleteKolCollection: builder.mutation<void, DeleteKolCollectionRequest>({
      query: ({ id }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}`, 'v3'),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.Collections,
        WorkspacesAPITagType.KolRadarSearch,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.collection,
        },
      ],
    }),
    updateKolCollection: builder.mutation<void, UpdateKolCollectionRequest>({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}`, 'v3'),
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.Collection,
        WorkspacesAPITagType.Collections,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.collection,
        },
      ],
    }),
    addKolsToKolCollection: builder.mutation<
      void,
      AddKolsToKolCollectionRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}/kols`, 'v3'),
          method: 'Post',
          body,
        }
      },
      async onQueryStarted(
        { kol_ids },
        { getState, dispatch, queryFulfilled },
      ) {
        try {
          await queryFulfilled
          updateKolCollectionKolQueryData(
            getState() as RootState,
            dispatch,
            kol_ids,
            true,
          )
        } catch (error) {
          logger.error(error)
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.Collection,
        WorkspacesAPITagType.Collections,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolCollectionInfo,
      ],
    }),
    fetchKolCollection: builder.query<
      FetchKolCollectionResponse,
      FetchKolCollectionRequest
    >({
      query: ({ id }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}`, 'v3'),
        }
      },
      extraOptions: {
        dataSchema: kolCollectionSchema,
      },
      providesTags: [WorkspacesAPITagType.Collection],
    }),
    fetchKolCollections: builder.query<
      FetchKolCollectionsResponse,
      FetchKolCollectionsRequest
    >({
      query: (params) => {
        const PAGE_SIZE = 10
        const payload = removeNil({
          ...params,
          per_page: PAGE_SIZE,
          stringify: true,
          array_format: 'none',
        })

        return {
          url: getWorkspaceApiUrl('/kol-collections', 'v3'),
          params: payload,
        }
      },
      extraOptions: {
        dataSchema: fetchKolCollectionsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.Collections],
    }),
    fetchKolCollectionInfinite: builder.infiniteQuery<
      FetchKolCollectionsResponse,
      FetchKolCollectionsInfiniteRequest,
      FetchKolCollectionsRequest['page']
    >({
      query: ({ queryArg, pageParam }) => {
        const PAGE_SIZE = 10

        const params = {
          ...queryArg,
          page: pageParam,
          per_page: PAGE_SIZE,
          stringify: true,
          array_format: 'none',
        }

        return {
          url: getWorkspaceApiUrl('/kol-collections', 'v3'),
          params: removeNil(params),
        }
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.total <= lastPageParam * lastPage.per_page) {
            return null
          }

          return lastPageParam + 1
        },
      },
      extraOptions: {
        dataSchema: fetchKolCollectionsResponseSchema,
      },
      providesTags: (_result, _error, arg) =>
        arg?.kol_uuid
          ? [{ type: WorkspacesAPITagType.Collections, id: arg.kol_uuid }]
          : [WorkspacesAPITagType.Collections],
    }),
    duplicateKolCollection: builder.mutation<
      void,
      DuplicateKolCollectionRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}/duplicate`, 'v3'),
          method: 'POST',
          body,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.Collections,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.collection,
        },
      ],
    }),
    deleteKolsFromKolCollection: builder.mutation<
      void,
      DeleteKolsFromKolCollectionRequest
    >({
      query: ({ id, collection_kol_ids, kol_ids }) => {
        const body = { collection_kol_ids, kol_ids }
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}/kols`, 'v3'),
          method: 'DELETE',
          body,
        }
      },
      async onQueryStarted(
        { kol_ids, isCollectionKol },
        { getState, dispatch, queryFulfilled },
      ) {
        try {
          await queryFulfilled
          updateKolCollectionKolQueryData(
            getState() as RootState,
            dispatch,
            kol_ids,
            !!isCollectionKol,
          )
        } catch (error) {
          logger.error(error)
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.Collection,
        WorkspacesAPITagType.Collections,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolCollectionInfo,
      ],
    }),
    fetchKolCollectionKols: builder.query<
      FetchKolCollectionKolsResponse,
      FetchKolCollectionKolsRequest
    >({
      query: ({ id, ...params }) => {
        return {
          url: getWorkspaceApiUrl(`/kol-collections/${id}/kols`, 'v3'),
          params: removeNil({ ...params, ...defaultStringifyRequest }),
        }
      },
      extraOptions: {
        dataSchema: fetchKolCollectionKolsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.KolCollectionKols],
    }),
    fetchKolCollectionSimpleKols: builder.query<
      FetchKolCollectionSimpleKolsResponse,
      FetchKolCollectionSimpleKolsRequest
    >({
      query: ({ id, ...params }) => {
        return {
          url: getWorkspaceApiUrl(
            `/kol-collections/${id}/simple-kol-list`,
            'v3',
          ),
          params: removeNil({ ...params, ...defaultStringifyRequest }),
        }
      },
      extraOptions: {
        dataSchema: fetchKolCollectionSimpleKolsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.KolCollectionKols],
    }),
    fetchKolCollectionSetting: builder.query<
      FetchKolCollectionSettingResponse,
      FetchKolCollectionSettingRequest
    >({
      query: ({ collectionId }) => {
        return {
          url: getWorkspaceApiUrl(
            `/kol-collections/${collectionId}/collection-setting`,
            'v3',
          ),
        }
      },
      extraOptions: {
        dataSchema: fetchKolCollectionSettingResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.CollectionSettings],
    }),
    setKolCollectionSetting: builder.mutation<
      void,
      SetKolCollectionSettingRequest
    >({
      query: ({ id, payload }) => ({
        url: getWorkspaceApiUrl(
          `/kol-collections/${id}/collection-setting`,
          'v3',
        ),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [WorkspacesAPITagType.CollectionSettings],
    }),
    exportKolCollection: builder.query<ArrayBuffer, ExportKolCollectionRequest>(
      {
        query: ({ id }) => ({
          url: getWorkspaceApiUrl(`/kol-collections/${id}/export`, 'v3'),
          responseHandler: (response): Promise<ArrayBuffer> =>
            response.arrayBuffer(),
        }),
      },
    ),
  }),
  overrideExisting: false,
})

export const useDeleteKolCollectionMutation =
  kolCollectionsApi.endpoints.deleteKolCollection.useMutation

export const useUpdateKolCollectionMutation =
  kolCollectionsApi.endpoints.updateKolCollection.useMutation

export const useFetchKolCollectionInfiniteQuery =
  kolCollectionsApi.endpoints.fetchKolCollectionInfinite.useInfiniteQuery

export const useDuplicateKolCollectionMutation =
  kolCollectionsApi.endpoints.duplicateKolCollection.useMutation

export const useFetchKolCollectionKolsQuery =
  kolCollectionsApi.endpoints.fetchKolCollectionKols.useQuery

export const useFetchKolCollectionSimpleKolsQuery =
  kolCollectionsApi.endpoints.fetchKolCollectionSimpleKols.useQuery

export const useSetKolCollectionSettingMutation =
  kolCollectionsApi.endpoints.setKolCollectionSetting.useMutation

export const useLazyExportKolCollectionQuery =
  kolCollectionsApi.endpoints.exportKolCollection.useLazyQuery

export const useFetchKolCollectionQuery =
  kolCollectionsApi.endpoints.fetchKolCollection.useQuery

export const useFetchKolCollectionsQuery =
  kolCollectionsApi.endpoints.fetchKolCollections.useQuery

export const useFetchKolCollectionSettingQuery =
  kolCollectionsApi.endpoints.fetchKolCollectionSetting.useQuery

export const useCreateKolCollectionMutation =
  kolCollectionsApi.endpoints.createKolCollection.useMutation

export const useAddKolsToKolCollectionMutation =
  kolCollectionsApi.endpoints.addKolsToKolCollection.useMutation

export const useDeleteKolsFromKolCollectionMutation =
  kolCollectionsApi.endpoints.deleteKolsFromKolCollection.useMutation
